import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DateRangePicker from "../../../components/common/DateRangePicker";
import { useDispatch, useSelector } from "react-redux";
import { getMinMaxDate } from "components/common/Utils";
import { getOverviewDashboard24_7_Chart } from "store/actions/dashboardSlice";
import IsLoading from "components/common/IsLoading";
import { ShimmerThumbnail } from "react-shimmer-effects";

const CommonBookingTimes = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { loading3, commonBookingTime } = useSelector(
    (state) => state.Dashboards
  );

  const [dates, setDates] = useState(null);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD

    const isReset = actionType === "RESET";
    const payload = {
      spaceTypeInt: [], // [] for all , 1 - workspace, 2- meetingroom, 3 - workshop
      branches: [],
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getOverviewDashboard24_7_Chart(payload));
  };

  const handleReset = () => {
    setDates([]);
    getAllData({ actionType: "RESET" });
  };

  // Static percentage matrix [dayIndex][hourIndex]
  // const percentageMatrix = [
  //   [
  //     50, 90, 15, 5, 0, 0, 0, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 80, 70, 60,
  //     50, 40, 30, 20,
  //   ], // Sun
  //   [
  //     0, 5, 85, 5, 0, 0, 5, 10, 15, 25, 35, 45, 55, 65, 75, 85, 95, 90, 85, 75,
  //     65, 55, 45, 35,
  //   ], // Mon
  //   [
  //     5, 10, 15, 10, 5, 5, 5, 10, 15, 25, 30, 35, 45, 55, 65, 75, 85, 80, 70,
  //     60, 50, 40, 30, 20,
  //   ], // Tue
  //   [
  //     0, 5, 5, 5, 0, 0, 0, 5, 10, 15, 20, 25, 35, 45, 55, 65, 75, 70, 60, 50,
  //     40, 30, 20, 10,
  //   ], // Wed
  //   [
  //     5, 5, 5, 0, 0, 0, 5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 85, 80, 70,
  //     60, 50, 40, 30,
  //   ], // Thu
  //   [
  //     10, 15, 20, 10, 5, 0, 0, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 85, 75,
  //     65, 55, 45, 35, 25,
  //   ], // Fri
  //   [
  //     15, 20, 25, 20, 15, 10, 10, 15, 20, 25, 35, 45, 55, 65, 75, 85, 95, 90,
  //     85, 75, 65, 55, 45, 35,
  //   ], // Sat
  // ];

  const getBackgroundColor = (percentage) => {
    if (percentage > 80) return { bg: "#C4D26E", color: "#FFFFFF" };
    if (percentage > 60) return { bg: "#DCE1BB", color: "#A4A4A4" };
    if (percentage > 30) return { bg: "#F1F3E4", color: "#A4A4A4" };
    if (percentage > 0) return { bg: "#EFEFEF", color: "#A4A4A4" };
    return "";
  };

  return (
    <>
      {loading3 ? (
        <ShimmerThumbnail height={446} className="m-0" rounded />
      ) : (
        <div className="inner_section">
          <div className="g_between gap-3">
            <h2 className="fs_20">{t("Common Booking Times")}</h2>
            <div className="d-flex gap-3 common_filter">
              <div className="buttons">
                <button
                  className="search_btn"
                  onClick={() => getAllData({ actionType: "SEARCH" })}
                >
                  {t("Search")}
                </button>
                <button className="reset_btn" onClick={handleReset}>
                  {t("Reset")}
                </button>
              </div>
              <DateRangePicker dates={dates} setDates={setDates} />
            </div>
          </div>
          <div className="content">
            <div className="day_row g_between align-items-end">
              <div className="d-flex">
                <div>
                  <h2 className="fs_28 text_primary">
                    {commonBookingTime?.mostBookedDay}
                  </h2>
                  <p className="fs_14 text_gray">{t("Most booked day")}</p>
                </div>
                <div className="ms-5">
                  <h2 className="fs_28 text_primary">
                    {commonBookingTime?.mostBookedHour}
                  </h2>
                  <p className="fs_14 text_gray">{t("Most booked hour")}</p>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <span className="fs_14">{t("Less")}</span>
                <div className="mx-2 opacity_div ">
                  {[0, 1, 2, 3].map((item, index) => (
                    <span key={index}></span>
                  ))}
                </div>
                <span className="fs_14">{t("More")}</span>
              </div>
            </div>
          </div>

          {/* Time grid */}
          <div className="w-100 overflow-x-auto pb-2">
            <div style={{ minWidth: 1250 }}>
              {/* Time header */}
              <div className="hours_row">
                {commonBookingTime?.hours?.map((hour, index) => (
                  <div
                    key={index}
                    className={`hour_child ${
                      index >= 11 ? "right_section" : "left_section"
                    } ${index === 11 ? "separator" : ""}`}
                  >
                    <h6
                      className={`hour_text fs_14 ${
                        index === 0 || index === 11 ? "text_black" : ""
                      }`}
                    >
                      {hour}
                    </h6>
                  </div>
                ))}
              </div>

              {/* Days and time slots */}
              {commonBookingTime?.days?.map((day, dayIndex) => (
                <div key={dayIndex} className="row g-0">
                  <div className="col">
                    <div className="d-flex">
                      <div className="w_fit_content">
                        <div className="fs_14 day_text">{day}</div>
                      </div>
                      <div className="day_per_row">
                        {commonBookingTime?.hours?.map((hour, hourIndex) => {
                          const percentage =
                            commonBookingTime?.percentageMatrix[dayIndex][
                              hourIndex
                            ];
                          // console.log(percentage)
                          return (
                            <div
                              key={hourIndex}
                              className={`percent_value ${
                                hourIndex === 11 ? "separator" : ""
                              }`}
                              style={{
                                backgroundColor:
                                  getBackgroundColor(percentage)?.bg,
                                color: getBackgroundColor(percentage)?.color,
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                              }}
                            >
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="hour-percentage">
                                    {`${day}, Hour ${hour} — ${percentage}%`}
                                  </Tooltip>
                                }
                              >
                                <div className="text-center fs_12">
                                  {percentage}%
                                </div>
                              </OverlayTrigger>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommonBookingTimes;
